/**
 * Status toggle for main tasks that have no subtasks.
 *
 * A main task's status is normally derived from its subtasks
 * (`TaskService.derive_parent_status`), so a task created without any would
 * otherwise be stuck on `PENDING` forever. This hook backs the checkmark
 * control on `TaskSummaryCard`, which is the only way to move such a task.
 *
 * `onChanged` is called after every attempt, successful or not, so the caller
 * can refetch its list — on failure that also resyncs the card back to the
 * true status.
 */

import { useCallback } from "react";
import { TaskStatus } from "@/types";
import { useTaskMutations } from "./useTasks";

export type TaskCompletionToggle = (
  taskId: string,
  status: TaskStatus,
  version: number,
) => Promise<number | undefined>;

export function useTaskCompletionToggle(
  onChanged?: () => void,
): TaskCompletionToggle {
  const { updateTaskStatus } = useTaskMutations();

  return useCallback(
    async (taskId, status, version) => {
      try {
        const result = await updateTaskStatus(taskId, status, version);
        if (!result?.success) {
          console.error("Task completion toggle failed:", result?.message);
          onChanged?.();
          return undefined;
        }
        onChanged?.();
        // Hand the fresh version back so a chained click doesn't send a stale
        // one and get rejected by optimistic locking.
        return result.task?.version as number | undefined;
      } catch (error) {
        console.error("Task completion toggle failed:", error);
        onChanged?.();
        return undefined;
      }
    },
    [updateTaskStatus, onChanged],
  );
}
